/* eslint-disable @typescript-eslint/no-explicit-any */
import { createReducer } from 'deox';
import produce from 'immer';
import { CatalogState } from './types';
import {
  getCatalogAction,
  getGroupsAction,
  getModelsAction,
  getModificationAction,
  getPartsAction,
  getScheme,
  getSubGroupsAction,
  resetCatalogData,
  searchByVINAction,
  setAllMarksAction,
  setCarGroupsAction,
  setCarMarkAction,
  setCarModelAction,
  setCarModificationAction,
  setCarParentGroupAction,
  setCarTypeAction,
  setCatalogStepAction,
} from './actions';
import { Steps } from '../../constants/catalogsSteps';

// Вспомогательная функция для загрузки состояния автомобиля из localStorage
const loadCarStateFromLocalStorage = () => {
  try {
    const carState = localStorage.getItem('carState');
    return carState ? JSON.parse(carState) : null;
  } catch (e) {
    console.error('Error reading carState from localStorage', e);
    return null;
  }
};

// Загружаем данные из localStorage
const carStateFromLocalStorage = loadCarStateFromLocalStorage();

const initialState: CatalogState = {
  isLoading: {
    searchByVIN: false,
    catalog: false,
    model: false,
    modification: false,
    groups: false,
    subGroups: false,
    parts: false,
    scheme: false,
  },
  errors: {
    searchByVIN: null,
    catalog: null,
    model: null,
    modification: null,
    groups: null,
    subGroups: null,
    parts: null,
    scheme: null,
  },
  searchByVIN: null,
  typesList: null,
  marksList: null,
  modelsList: null,
  modificationsList: null,
  groupsList: [],
  subGroupsList: [],
  parts: null,
  scheme: null,
  selectedCar: {
    type: carStateFromLocalStorage?.type || '', // Загружаем тип из localStorage
    mark: carStateFromLocalStorage?.mark || '', // Загружаем марку из localStorage
    model: carStateFromLocalStorage?.model || '', // Загружаем модель из localStorage
    modification: carStateFromLocalStorage?.modification || '', // Загружаем модификацию из localStorage
    group: carStateFromLocalStorage?.group || '', // Загружаем группу из localStorage
    parentGroup: carStateFromLocalStorage?.parentGroup || '', // Загружаем родительскую группу из localStorage
    step: carStateFromLocalStorage?.step || 1, // Загружаем шаг из localStorage
  },
  // selectedMark: null,
};

const saveStateToLocalStorage = (state: any) => {
  localStorage.setItem('carState', JSON.stringify(state));
};

export const catalogReducer = createReducer(initialState, handle => [
  // --------- SearchByVin ---------
  handle(searchByVINAction.request, state =>
    produce(state, draft => {
      draft.searchByVIN = null;
      draft.isLoading.searchByVIN = true;
      draft.errors.searchByVIN = null;
    }),
  ),
  handle(searchByVINAction.success, (state, { payload }) =>
    produce(state, draft => {
      draft.searchByVIN = payload;
      draft.errors.searchByVIN = null;
      draft.isLoading.searchByVIN = false;
    }),
  ),
  handle(searchByVINAction.failure, (state, { payload }) =>
    produce(state, draft => {
      draft.isLoading.searchByVIN = false;
      draft.errors.searchByVIN = payload;
    }),
  ),

  // --------- Catalogs ---------

  handle(getCatalogAction.request, state =>
    produce(state, draft => {
      draft.isLoading.catalog = true;
      draft.errors.catalog = null;
      draft.selectedCar.type = '';
    }),
  ),
  handle(getCatalogAction.success, (state, { payload }) =>
    produce(state, draft => {
      draft.typesList = payload;
      draft.isLoading.catalog = false;
      draft.errors.catalog = null;
    }),
  ),
  handle(getCatalogAction.failure, (state, { payload }) =>
    produce(state, draft => {
      draft.isLoading.catalog = false;
      draft.errors.catalog = payload;
    }),
  ),

  // --------- Models ---------

  handle(getModelsAction.request, state =>
    produce(state, draft => {
      draft.modelsList = null;
      draft.isLoading.model = true;
      draft.errors.model = null;
    }),
  ),
  handle(getModelsAction.success, (state, { payload }) =>
    produce(state, draft => {
      draft.modelsList = payload;
      draft.isLoading.model = false;
      draft.errors.model = null;
    }),
  ),
  handle(getModelsAction.failure, (state, { payload }) =>
    produce(state, draft => {
      draft.isLoading.model = false;
      draft.errors.model = payload;
    }),
  ),

  // --------- Modifications ---------

  handle(getModificationAction.request, state =>
    produce(state, draft => {
      draft.modificationsList = null;
      draft.isLoading.modification = true;
      draft.errors.modification = null;
    }),
  ),
  handle(getModificationAction.success, (state, { payload }) =>
    produce(state, draft => {
      if (draft.modificationsList) {
        draft.modificationsList.modifications = [
          ...draft.modificationsList.modifications,
          ...payload.modifications,
        ];
      } else {
        draft.modificationsList = payload;
      }

      draft.isLoading.modification = false;
      draft.errors.modification = null;
    }),
  ),

  handle(getModificationAction.failure, (state, { payload }) =>
    produce(state, draft => {
      draft.isLoading.modification = false;
      draft.errors.modification = payload;
    }),
  ),

  // --------- Groups ---------
  handle(getGroupsAction.request, state =>
    produce(state, draft => {
      draft.groupsList = [];
      draft.isLoading.groups = true;
      draft.errors.groups = null;
    }),
  ),
  handle(getGroupsAction.success, (state, { payload }) =>
    produce(state, draft => {
      draft.groupsList = payload;
      draft.isLoading.groups = false;
      draft.errors.groups = null;
    }),
  ),
  handle(getGroupsAction.failure, (state, { payload }) =>
    produce(state, draft => {
      draft.isLoading.groups = false;
      draft.errors.groups = payload;
    }),
  ),

  // --------- SubGroups ---------
  handle(getSubGroupsAction.request, state =>
    produce(state, draft => {
      draft.subGroupsList = [];
      draft.isLoading.subGroups = true;
      draft.errors.subGroups = null;
    }),
  ),
  handle(getSubGroupsAction.success, (state, { payload }) =>
    produce(state, draft => {
      draft.errors.subGroups = null;
      draft.subGroupsList = payload;
      draft.isLoading.subGroups = false;
    }),
  ),
  handle(getSubGroupsAction.failure, (state, { payload }) =>
    produce(state, draft => {
      draft.isLoading.subGroups = false;
      draft.errors.subGroups = payload;
    }),
  ),

  // --------- PARTS ---------
  handle(getPartsAction.request, state =>
    produce(state, draft => {
      draft.parts = null;
      draft.isLoading.parts = true;
      draft.isLoading.scheme = true;
      draft.errors.parts = null;
    }),
  ),
  handle(getPartsAction.success, (state, { payload }) =>
    produce(state, draft => {
      draft.errors.parts = null;
      draft.parts = payload;
      draft.isLoading.parts = false;
      draft.isLoading.scheme = false;
    }),
  ),
  handle(getPartsAction.failure, (state, { payload }) =>
    produce(state, draft => {
      draft.isLoading.parts = false;
      draft.isLoading.scheme = false;
      draft.errors.parts = payload;
    }),
  ),

  // --------- SCHEME ---------
  handle(getScheme.request, state =>
    produce(state, draft => {
      draft.scheme = null;
      draft.isLoading.scheme = true;
      draft.errors.scheme = null;
    }),
  ),
  handle(getScheme.success, (state, { payload }) =>
    produce(state, draft => {
      draft.errors.scheme = null;
      draft.scheme = payload;
      draft.isLoading.scheme = false;
    }),
  ),
  handle(getScheme.failure, (state, { payload }) =>
    produce(state, draft => {
      draft.isLoading.scheme = false;
      draft.errors.scheme = payload;
    }),
  ),

  // --------- Set & Get ---------
  handle(setAllMarksAction, (state, { payload }) =>
    produce(state, draft => {
      draft.marksList = payload;
    }),
  ),

  handle(resetCatalogData, () => initialState),

  // --------- Selected Car Items  ---------

  // Экшен для сброса состояния автомобиля
  handle(resetCatalogData, state =>
    produce(state, draft => {
      // saveStateToLocalStorage(draft.selectedCar);
      draft.selectedCar = {
        type: '',
        mark: '',
        model: '',
        modification: '',
        group: '',
        parentGroup: '',
        step: 1,
      };
    }),
  ),

  handle(setCarTypeAction, (state, { payload }) =>
    produce(state, draft => {
      draft.selectedCar.type = payload;
      saveStateToLocalStorage(draft.selectedCar);
    }),
  ),
  handle(setCarMarkAction, (state, { payload }) =>
    produce(state, draft => {
      draft.selectedCar.mark = payload;
      saveStateToLocalStorage(draft.selectedCar);
    }),
  ),
  handle(setCarModelAction, (state, { payload }) =>
    produce(state, draft => {
      draft.selectedCar.model = payload;
      saveStateToLocalStorage(draft.selectedCar);
    }),
  ),
  handle(setCarModificationAction, (state, { payload }) =>
    produce(state, draft => {
      draft.selectedCar.modification = payload;
      saveStateToLocalStorage(draft.selectedCar);
    }),
  ),
  handle(setCarGroupsAction, (state, { payload }) =>
    produce(state, draft => {
      draft.selectedCar.group = payload;
      saveStateToLocalStorage(draft.selectedCar);
    }),
  ),
  handle(setCarParentGroupAction, (state, { payload }) =>
    produce(state, draft => {
      draft.selectedCar.parentGroup = payload;
      saveStateToLocalStorage(draft.selectedCar);
    }),
  ),
  handle(setCatalogStepAction, (state, { payload }) =>
    produce(state, draft => {
      // Устанавливаем текущий шаг
      draft.selectedCar.step = payload;

      // В зависимости от текущего шага очищаем данные
      switch (payload) {
        case Steps.CarTypesList:
          draft.selectedCar.type = undefined;
          draft.selectedCar.mark = undefined;
          draft.selectedCar.model = undefined;
          draft.selectedCar.modification = undefined;
          draft.selectedCar.group = undefined;
          draft.selectedCar.parentGroup = undefined;
          break;

        case Steps.ModelSelection:
          draft.selectedCar.modification = undefined;
          draft.selectedCar.group = undefined;
          draft.selectedCar.parentGroup = undefined;
          break;

        case Steps.ModificationsList:
          draft.selectedCar.modification = undefined;
          draft.selectedCar.group = undefined;
          draft.selectedCar.parentGroup = undefined;
          break;

        case Steps.GroupList:
          draft.selectedCar.group = undefined;
          draft.selectedCar.parentGroup = undefined;
          break;

        // case Steps.Scheme:
        //   draft.selectedCar.group = undefined;
        //   draft.selectedCar.parentGroup = undefined;
        //   break;

        // case Steps.Scheme:
        //   draft.selectedCar.mark = undefined;
        //   draft.selectedCar.model = undefined;
        //   draft.selectedCar.modification = undefined;
        //   draft.selectedCar.group = undefined;
        //   break;

        // case Steps.Filters:
        //   // Очищаем только данные выше фильтров, родительская группа остаётся
        //   draft.selectedCar.type = undefined;
        //   draft.selectedCar.mark = undefined;
        //   draft.selectedCar.model = undefined;
        //   draft.selectedCar.modification = undefined;
        //   draft.selectedCar.group = undefined;
        //   break;

        default:
          break;
      }

      // Сохраняем обновлённое состояние в localStorage
      saveStateToLocalStorage(draft.selectedCar);
    }),
  ),
]);
